import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';

import { TradingCalendarEntry } from '@flipster/apis/v2/market/types';
import { MessageKey } from '@flipster/i18n';
import { getTimezoneOffset, timestampToDate } from '@flipster/utils/datetime';

export const formatDatePrefix = (date: Date): string => format(date, 'M/d');
const fixedEndTime = '24:00';

export const dayTranslationKeys = [
  'Autogen_common.r251126.s_Sun',
  'Autogen_common.r251126.s_Mon',
  'Autogen_common.r251126.s_Tue',
  'Autogen_common.r251126.s_Wed',
  'Autogen_common.r251126.s_Thu',
  'Autogen_common.r251126.s_Fri',
  'Autogen_common.r251126.s_Sat',
] as const satisfies MessageKey[];

export const formatDateDayKey = (date: Date): number => date.getDay();

export const formatTimeRange = (start: Date, end: Date | null): string => {
  const startTime = format(start, 'HH:mm');
  if (!end) return `${startTime} -`;
  const isEndOfDay = end.getHours() === 23 && end.getMinutes() === 59;
  const endTime = isEndOfDay ? fixedEndTime : format(end, 'HH:mm');
  return `${startTime} - ${endTime}`;
};

type TimeWindow = { start: Date; end: Date | null };

/**
 * 자정(midnight) 경계에서 윈도우를 날짜별 세그먼트로 분할
 * e.g. 4/1 07:00 → 4/2 06:00 => [4/1 07:00→23:59, 4/2 00:00→06:00]
 */
export const splitWindowByDay = (start: Date, end: Date | null): TimeWindow[] => {
  if (!end || isSameDay(start, end)) return [{ start, end }];

  return eachDayOfInterval({ start, end }).map((day) => ({
    start: isAfter(day, start) ? startOfDay(day) : start,
    end: isSameDay(day, end) ? end : endOfDay(day),
  }));
};

export const sessionLabels: Record<string, string> = {
  PRE_MARKET: 'Pre-market',
  REGULAR: 'Regular',
  AFTER_MARKET: 'After-market',
  OVER_NIGHT: 'Overnight',
  WEEKEND: 'Weekend',
  HOLIDAY: 'Holiday',
  MAINTENANCE: 'Maintenance',
};

type SessionWindow = { start: Date; end: Date | null };

export type DailySchedule = {
  date: Date;
  windows: TimeWindow[];
};

/**
 * 향후 N일간의 거래 가능 시간을 날짜별로 그룹화하여 반환
 *
 * 파이프라인:
 * 1. calendar entries → tradable 세션만 필터 → { start, end } 윈도우로 변환
 * 2. 자정 경계로 분할 (e.g. 4/1 22:00~4/2 06:00 → [4/1 22:00~23:59, 4/2 00:00~06:00])
 * 3. 날짜별 그룹화
 */
const mergeAdjacentWindows = (windows: TimeWindow[]): TimeWindow[] => {
  if (windows.length <= 1) return windows;
  return windows.reduce<TimeWindow[]>((acc, curr) => {
    const prev = acc[acc.length - 1];
    if (prev && prev.end !== null && prev.end.getTime() === curr.start.getTime()) {
      acc[acc.length - 1] = { start: prev.start, end: curr.end };
    } else {
      acc.push(curr);
    }
    return acc;
  }, []);
};

export const getWeeklySchedule = (
  entries: TradingCalendarEntry[],
  tradableSessions: string[],
  now: number,
): DailySchedule[] => {
  const rangeStart = startOfDay(new Date(now));
  const lastEntry = entries.at(-1);
  const displayedDays = lastEntry ? differenceInCalendarDays(timestampToDate(lastEntry.timestamp), rangeStart) + 1 : 7;
  const rangeEnd = addDays(rangeStart, displayedDays - 1);

  // 1. entries → tradable 윈도우 추출
  const toWindow = (entry: TradingCalendarEntry, i: number) => {
    const entryStart = timestampToDate(entry.timestamp);
    const start = isAfter(entryStart, rangeStart) ? entryStart : rangeStart;
    const end = entries[i + 1] ? timestampToDate(entries[i + 1].timestamp) : endOfDay(entryStart);
    return { start, end };
  };

  const tradableWindows = entries
    .map((entry, i) => ({ entry, ...toWindow(entry, i) }))
    .filter(
      ({ entry, start, end }) =>
        tradableSessions.includes(entry.status) && isBefore(start, rangeEnd) && isBefore(start, end),
    );

  // 2. 자정 경계 분할 → 3. 날짜별 그룹화
  const nowDate = new Date(now);
  const byDate = new Map<string, DailySchedule>();
  for (const { start, end } of tradableWindows
    .flatMap((w) => splitWindowByDay(w.start, w.end))
    .filter((w) => isBefore(w.start, rangeEnd) && w.end != null && isAfter(w.end, nowDate))) {
    const key = format(start, 'yyyy-MM-dd');
    const daily = byDate.get(key) ?? { date: startOfDay(start), windows: [] };
    daily.windows.push({ start, end });
    byDate.set(key, daily);
  }

  return [...byDate.values()]
    .sort((a, b) => a.date.getTime() - b.date.getTime())
    .map((daily) => ({ ...daily, windows: mergeAdjacentWindows(daily.windows) }));
};

export const isTimestampTradable = (timestamp: Date, sessionWindows: SessionWindow[]): boolean => {
  if (sessionWindows.length === 0) return true;
  return sessionWindows.some(
    ({ start, end }) => !isBefore(timestamp, start) && (end === null || isBefore(timestamp, end)),
  );
};

export const formatSessionWindow = (start: Date, end: Date | null): string => {
  const startTime = format(start, 'HH:mm');
  const tzLabel = getTimezoneOffset();

  if (!end) return `${startTime} - ${tzLabel}`;
  const endTime = format(end, 'HH:mm');

  if (!isSameDay(start, end)) {
    return `${format(start, 'EEE')} ${startTime} - ${format(end, 'EEE')} ${endTime} ${tzLabel}`;
  }

  return `${startTime} - ${endTime} ${tzLabel}`;
};
